import { makeStyles } from '@material-ui/core';
import React from 'react';
import { MenuPrintConfig } from './types';
import { getTapLocation } from '../../../util/tap';
import { getServingPriceLabel, getServingSizeLabel } from '../../../util/lang';
import { sortServings } from '../../../util/servings';

interface NewYorkTapProps {
  tap: any;
  config: MenuPrintConfig;
  bordered?: boolean;
  grouped?: boolean;
}

const NewYorkTap: React.FC<NewYorkTapProps> = ({ tap, config, bordered = false, grouped = false }) => {
  const classes = useStyles();
  const {
    showDescription,
    showTapLocation,
    showHandle,
    showBreweryName,
    showServings,
    showServingSize,
    showServingCurrency,
    showBeerStyle
  } = config;

  const tapLocation = getTapLocation(tap);
  const breweryName = tap.beverage?.source?.name?.split('(')[0]?.trim();
  const showServingsRow = showServings && !!tap.servings?.length;

  const containerStyle: React.CSSProperties = bordered
    ? {
        borderBottom: '1px solid #000',
        padding: '10px 10px',
        breakInside: 'avoid',
        pageBreakInside: 'avoid'
      }
    : grouped
    ? {
        padding: '10px 0',
        breakInside: 'avoid',
        pageBreakInside: 'avoid'
      }
    : {
        padding: '8px 0 10px 0',
        marginBottom: 6,
        breakInside: 'avoid',
        pageBreakInside: 'avoid'
      };

  const detailParts: string[] = [];
  if (showBeerStyle && tap.beverage?.style) {
    detailParts.push(tap.beverage.style);
  }
  if (tap.showABV !== false && tap.beverage?.abv != null) {
    detailParts.push(`${tap.beverage.abv}% ABV`);
  }
  if (tap.showIBU === true && tap.beverage?.ibu != null) {
    detailParts.push(`${tap.beverage.ibu} IBUs`);
  }
  if (showTapLocation && tapLocation) {
    detailParts.push(tapLocation);
  }

  const servingsText = showServingsRow
    ? sortServings(tap.servings)
        .map((s: any) => {
          const sizeLabel = showServingSize ? getServingSizeLabel(s).replace(' ', '') : '';
          const priceLabel = showServingCurrency ? getServingPriceLabel(s, true) : `$${getServingPriceLabel(s, false)}`;
          return sizeLabel ? `${sizeLabel} ${priceLabel}` : priceLabel;
        })
        .join(' \u00B7 ')
    : '';

  return (
    <div style={containerStyle}>
      <p className={classes.titleLine}>
        {showHandle && <span className={classes.handle}>{tap.name}.</span>}
        {showBreweryName && breweryName && <span className={classes.brewery}>{breweryName.toUpperCase()}</span>} {tap.beverage?.name}
      </p>

      {detailParts.length > 0 && <p className={classes.detailLine}>{detailParts.join(' \u00B7 ')}</p>}

      {showDescription && tap.beverage?.description && <p className={classes.descriptionLine}>{tap.beverage.description}</p>}

      {showServingsRow && <p className={classes.servingsLine}>{servingsText}</p>}
    </div>
  );
};

const useStyles = makeStyles(() => ({
  titleLine: {
    fontWeight: 700,
    fontSize: 16,
    lineHeight: '20px',
    margin: 0,
    fontFamily: 'system-ui, -apple-system, sans-serif'
  },
  handle: {
    marginRight: 6
  },
  brewery: {
    textTransform: 'uppercase' as const
  },
  detailLine: {
    fontSize: 12,
    lineHeight: '15px',
    margin: '3px 0 0 0',
    color: '#333',
    fontWeight: 400,
    fontFamily: 'system-ui, -apple-system, sans-serif'
  },
  descriptionLine: {
    fontSize: 11,
    lineHeight: '14px',
    margin: '3px 0 0 0',
    color: '#444',
    fontFamily: 'system-ui, -apple-system, sans-serif'
  },
  servingsLine: {
    fontWeight: 700,
    fontSize: 13,
    lineHeight: '17px',
    margin: '4px 0 0 0',
    fontFamily: 'system-ui, -apple-system, sans-serif'
  }
}));

export default NewYorkTap;
